import type { TopHoldingsDataResponse } from 'venn-api';
import React, { useMemo } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { createLabelColumn } from '../../../../studio-blocks/logic/columnParsers';
import { getComparisonLabelForBlockLegend, useHasFF } from 'venn-utils';
import HeaderCellRenderer from '../../../../studio-blocks/components/grid/renderers/HeaderCellRenderer';
import { getDefaultCellClass, getDefaultValueFormat } from '../../../../studio-blocks/logic/gridStyling';
import ValueCellRenderer from '../../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { useRecoilValue } from 'recoil';
import { blockLimitedHoldingsSubjectsWithFees, vdparFundIdsState } from 'venn-state';
import type { TopHoldingsRow } from '../../../../studio-blocks/types';
import { OptionIcon } from '../../../../common/OptionIcon';
import { usePageVdparSymbol } from '../../../components/core/block-subject-footnotes-and-superscripts';

const TOTAL_ROW_ID = 'total-row';

export const useTopHoldingsTable = (blockId: string, data: TopHoldingsDataResponse) => {
  const allSubjects = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(blockId));
  const vdparFundIds = useRecoilValue(vdparFundIdsState);
  const vdparSymbol = usePageVdparSymbol();
  const skipKSuffixInFormatting = useHasFF('skip_k_suffix_in_formatting_ff');

  const rowData: TopHoldingsRow[] | undefined = useMemo(() => {
    return data.topHoldings[0]?.holdings.map((holding) => {
      const { assetName, weight, optionData, fundId } = holding;
      return {
        id: assetName,
        key: assetName,
        label: assetName,
        optionData,
        type: 'PERCENTAGE',
        weight,
        fundId,
      };
    });
  }, [data]);

  const pinnedBottomRowData: TopHoldingsRow[] = useMemo(
    () => [
      {
        id: TOTAL_ROW_ID,
        key: TOTAL_ROW_ID,
        label: 'Total %MV in Top 10 Holdings',
        type: 'PERCENTAGE',
        weight: rowData?.length ? rowData?.map((row) => row.weight).reduce((a, b) => a + b) : 0,
      },
    ],
    [rowData],
  );

  const columnDefs = useMemo((): ColDef<TopHoldingsRow>[] => {
    return [
      {
        ...createLabelColumn({}),
        cellRenderer: ({ data, value }: ICellRendererParams<TopHoldingsRow>) => {
          const icon = data?.optionData ? <OptionIcon optionType={data.optionData.optionType} /> : null;
          return [
            value,
            data?.fundId && vdparFundIds.has(data.fundId) && <sup key="vdpar">{vdparSymbol}</sup>,
            icon && <span key="icon"> {icon}</span>,
          ];
        },
      },
      {
        headerName: getComparisonLabelForBlockLegend(allSubjects[0]!),
        headerComponent: HeaderCellRenderer,
        headerComponentParams: {
          subject: allSubjects[0]!,
        },
        valueGetter: (row) => row.data?.weight,
        valueFormatter: getDefaultValueFormat,
        cellRenderer: ValueCellRenderer,
        cellClass: getDefaultCellClass,
        autoHeight: false,
        cellRendererParams: {
          skipKSuffixInFormatting,
        },
      },
    ];
  }, [allSubjects, vdparFundIds, vdparSymbol, skipKSuffixInFormatting]);

  return { rowData, columnDefs, pinnedBottomRowData };
};
